import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, message,
  Card, Tag, Space, Popconfirm, Progress, Statistic, Row, Col, Tabs
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined,
  BarChartOutlined, SyncOutlined, EyeOutlined
} from '@ant-design/icons';
import api from '../utils/api';
import FbUsageCell from '../components/FbUsageCell';

const { Option } = Select;

const showSyncFeedback = (syncResult) => {
  if (!syncResult) return;
  const { ad_accounts: ads, pages, facebook_user: fbUser, warnings } = syncResult;
  if (fbUser?.name) {
    message.info(`Facebook 用户：${fbUser.name} (${fbUser.id})`);
  }
  if (ads || pages) {
    message.info(
      `关联结果：广告账户 ${ads?.linked ?? 0} 个，公共主页 ${pages?.linked ?? 0} 个`
    );
  }
  if (warnings?.length) {
    warnings.forEach((text) => message.warning(text, 6));
  }
};

const DeveloperAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [stats, setStats] = useState([]);
  const [bmList, setBmList] = useState([]);
  const [applicationList, setApplicationList] = useState([]);
  const [form] = Form.useForm();
  const [assetsVisible, setAssetsVisible] = useState(false);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [linkedAssets, setLinkedAssets] = useState(null);

  // 获取开发者账号列表
  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/business-managers/developer-accounts/list');
      console.log('API Response:', response.data);
      setAccounts(response.data || []);
    } catch (error) {
      console.error('Fetch error:', error);
      message.error('获取开发者账号失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取BM列表
  const fetchBMList = async () => {
    try {
      const response = await api.get('/business-managers/');
      setBmList(response.data || []);
    } catch (error) {
      console.error('Fetch BM list error:', error);
    }
  };

  const fetchApplicationList = async () => {
    try {
      const response = await api.get('/applications');
      setApplicationList(response.data || []);
    } catch (error) {
      console.error('Fetch applications error:', error);
    }
  };

  useEffect(() => {
    fetchAccounts();
    fetchBMList();
    fetchApplicationList();
  }, []);

  // 创建/更新开发者账号
  const handleSubmit = async (values) => {
    try {
      let response;
      if (editingAccount) {
        response = await api.put(
          `/business-managers/developer-accounts/${editingAccount.id}`,
          values
        );
        message.success('更新成功');
      } else {
        response = await api.post(
          '/business-managers/developer-accounts',
          values
        );
        message.success('创建成功');
      }
      showSyncFeedback(response.data?.sync_result);
      setModalVisible(false);
      form.resetFields();
      setEditingAccount(null);
      fetchAccounts();
    } catch (error) {
      message.error(error.response?.data?.detail || '操作失败');
    }
  };

  // 删除开发者账号
  const handleDelete = async (record) => {
    try {
      await api.delete(
        `/business-managers/developer-accounts/${record.id}`
      );
      message.success('删除成功');
      fetchAccounts();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 查看统计
  const handleViewStats = async () => {
    try {
      const response = await api.get('/business-managers/developer-accounts/stats');
      setStats(response.data);
      setStatsVisible(true);
    } catch (error) {
      message.error('获取统计失败');
    }
  };

  // 同步BM资产
  const handleSyncAssets = async (record) => {
    try {
      const response = await api.post(`/business-managers/developer-accounts/${record.id}/sync`);
      message.success('关联同步成功');
      showSyncFeedback(response.data?.sync_result);
      fetchAccounts();
    } catch (error) {
      message.error(error.response?.data?.detail || '同步失败');
    }
  };

  const handleViewAssets = async (record) => {
    setAssetsLoading(true);
    setAssetsVisible(true);
    setLinkedAssets(null);
    try {
      const response = await api.get(
        `/business-managers/developer-accounts/${record.id}/linked-assets`
      );
      setLinkedAssets(response.data);
    } catch (error) {
      message.error(error.response?.data?.detail || '获取关联资产失败');
      setAssetsVisible(false);
    } finally {
      setAssetsLoading(false);
    }
  };

  const adAccountColumns = [
    { title: '账户 ID', dataIndex: 'account_id', key: 'account_id' },
    { title: '名称', dataIndex: 'name', key: 'name' },
    {
      title: 'Meta API 用量',
      key: 'fb_usage',
      width: 320,
      render: (_, record) => (
        <FbUsageCell fbUsage={record.fb_usage} mode="account" />
      ),
    },
    {
      title: 'BM',
      dataIndex: 'bm_name',
      key: 'bm_name',
      render: (name) => name || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status === 'active' ? '正常' : status || '-'}
        </Tag>
      ),
    },
  ];

  const pageColumns = [
    { title: '主页 ID', dataIndex: 'page_id', key: 'page_id' },
    { title: '名称', dataIndex: 'name', key: 'name', render: (name) => name || '-' },
    {
      title: 'BM',
      dataIndex: 'bm_name',
      key: 'bm_name',
      render: (name) => name || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status === 'active' ? '正常' : status || '-'}
        </Tag>
      ),
    },
  ];

  // 打开编辑弹窗并加载凭证
  const handleEdit = async (record) => {
    try {
      setLoading(true);
      const response = await api.get(`/business-managers/developer-accounts/${record.id}`);
      setEditingAccount(record);
      form.setFieldsValue({
        name: response.data.name,
        access_token: response.data.access_token || '',
        priority: response.data.priority,
        weight: response.data.weight,
        daily_api_limit: response.data.daily_api_limit,
        bm_ids: response.data.bm_ids || [],
        application_id: response.data.application_id,
        status: response.data.status,
      });
      setModalVisible(true);
    } catch (error) {
      message.error('获取账号详情失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '关联应用',
      dataIndex: 'application_list',
      key: 'application_list',
      render: (appList) => {
        if (!appList || appList.length === 0) {
          return <Tag color="orange">未关联</Tag>;
        }
        return (
          <div>
            {appList.map((app) => (
              <Tag key={app.application_id} color="blue" style={{ marginBottom: 4 }}>
                {app.application_name}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Facebook 用户',
      key: 'facebook_user',
      render: (_, record) => (
        record.facebook_user_name ? (
          <div>
            <div>{record.facebook_user_name}</div>
            <div style={{ fontSize: 12, color: '#666' }}>{record.facebook_user_id}</div>
          </div>
        ) : '-'
      ),
    },
    {
      title: '关联资产',
      key: 'linked_assets',
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => handleViewAssets(record)} style={{ padding: 0 }}>
          账户 {record.linked_ad_account_count ?? 0} / 主页 {record.linked_page_count ?? 0}
        </Button>
      ),
    },
    {
      title: '关联 BM',
      dataIndex: 'bm_list',
      key: 'bm_list',
      render: (bmList) => {
        if (!bmList || bmList.length === 0) {
          return <Tag color="orange">未关联</Tag>;
        }
        return (
          <div>
            <Tag color="blue">{bmList.length} 个BM</Tag>
            <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
              {bmList.map(bm => bm.bm_name).join(', ')}
            </div>
          </div>
        );
      },
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      sorter: (a, b) => a.priority - b.priority,
    },
    {
      title: '权重',
      dataIndex: 'weight',
      key: 'weight',
    },
    {
      title: 'X-App-Usage',
      key: 'fb_usage',
      width: 180,
      render: (_, record) => (
        <FbUsageCell fbUsage={record.fb_usage} mode="developer" />
      ),
    },
    {
      title: 'API 配额',
      key: 'quota',
      render: (_, record) => (
        <Progress
          percent={Math.round((record.daily_api_calls / record.daily_api_limit) * 100)}
          size="small"
          status={record.daily_api_calls >= record.daily_api_limit ? 'exception' : 'active'}
          format={() => `${record.daily_api_calls}/${record.daily_api_limit}`}
        />
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '正常' : '停用'}
        </Tag>
      ),
    },
    {
      title: '最后使用',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      render: (date) => date ? new Date(date).toLocaleString() : '从未使用',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleViewAssets(record)}
          >
            查看
          </Button>
          <Button
            icon={<SyncOutlined />}
            onClick={() => handleSyncAssets(record)}
            disabled={!record.bm_ids || record.bm_ids.length === 0}
            title={record.bm_ids && record.bm_ids.length > 0 ? '同步 Facebook 关联' : '未关联BM'}
          >
            同步
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除吗？"
            onConfirm={() => handleDelete(record)}
          >
            <Button icon={<DeleteOutlined />} danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="开发者账号管理"
        extra={
          <Space>
            <Button
              icon={<BarChartOutlined />}
              onClick={handleViewStats}
            >
              统计
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingAccount(null);
                form.resetFields();
                setModalVisible(true);
              }}
            >
              添加账号
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={accounts}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      {/* 创建/编辑模态框 */}
      <Modal
        title={editingAccount ? '编辑开发者账号' : '添加开发者账号'}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingAccount(null);
        }}
        width={600}
      >
        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input placeholder="例如：开发者账号 1" />
          </Form.Item>
          <Form.Item
            name="application_id"
            label="关联应用"
            rules={[{ required: true, message: '请选择一个应用' }]}
            extra="App ID 和 App Secret 从关联应用获取"
          >
            <Select
              placeholder="请选择关联的应用"
              optionFilterProp="children"
            >
              {applicationList.map((app) => (
                <Option key={app.id} value={app.id}>
                  {app.name} ({app.app_id})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="access_token"
            label="User Access Token"
            rules={[{ required: true, message: '请输入 User Access Token' }]}
            extra={
              <div style={{ marginTop: 8 }}>
                <p style={{ margin: '0 0 4px 0', color: '#666' }}>
                  如何获取 Access Token：
                </p>
                <ol style={{ margin: 0, paddingLeft: 16, color: '#666', fontSize: 12 }}>
                  <li>访问 <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer">Graph API Explorer</a></li>
                  <li>选择你的应用</li>
                  <li>点击&quot;生成访问令牌&quot;</li>
                  <li>选择权限：ads_management, business_management</li>
                  <li>复制生成的 token</li>
                </ol>
              </div>
            }
          >
            <Input.TextArea placeholder="粘贴从 Graph API Explorer 获取的 Access Token" rows={3} />
          </Form.Item>
          
          <Form.Item>
            <Button 
              type="dashed" 
              onClick={async () => {
                const applicationId = form.getFieldValue('application_id');
                const selectedApp = applicationList.find((app) => app.id === applicationId);
                const appId = selectedApp?.app_id;
                if (!appId) {
                  message.warning('请先选择关联应用');
                  return;
                }
                try {
                  const detail = await api.get(`/applications/${selectedApp.id}`);
                  const appSecret = detail.data.app_secret;
                  if (!appSecret) {
                    message.warning('无法读取应用 App Secret');
                    return;
                  }
                  const response = await api.post('/business-managers/developer-accounts/get-access-token', null, {
                    params: { app_id: appId, app_secret: appSecret }
                  });
                  if (response.data.status === 'success') {
                    Modal.info({
                      title: '获取 Access Token 指引',
                      width: 600,
                      content: (
                        <div>
                          <p style={{ color: 'green' }}>✓ App ID 和 App Secret 验证通过</p>
                          <p>{response.data.note}</p>
                          {response.data.methods.map((method, idx) => (
                            <div key={idx} style={{ marginTop: 16 }}>
                              <h4>{method.name}</h4>
                              <ol>
                                {method.steps.map((step, sIdx) => (
                                  <li key={sIdx}>{step}</li>
                                ))}
                              </ol>
                            </div>
                          ))}
                        </div>
                      ),
                    });
                  } else {
                    message.error(response.data.message || '验证失败');
                  }
                } catch (error) {
                  message.error('验证失败: ' + (error.response?.data?.detail || error.message));
                }
              }}
            >
              验证并获取 Access Token 指引
            </Button>
          </Form.Item>
          

          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="优先级"
                initialValue={1}
              >
                <Input type="number" min={1} placeholder="数字越小优先级越高" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="weight"
                label="权重"
                initialValue={1}
              >
                <Input type="number" min={1} placeholder="负载均衡权重" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="daily_api_limit"
            label="每日 API 限额"
            initialValue={1000}
          >
            <Input type="number" min={1} />
          </Form.Item>
          <Form.Item
            name="bm_ids"
            label="关联 BM"
            rules={[{ required: true, message: '请至少选择一个 BM' }]}
            extra="添加前请先在 BM 管理同步总部数据（广告账户、公共主页）"
          >
            <Select
              mode="multiple"
              placeholder="请选择关联的 BM（必填）"
              optionFilterProp="children"
            >
              {bmList.map(bm => (
                <Option key={bm.id} value={bm.id}>
                  {bm.name} ({bm.bm_id})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="status"
            label="状态"
            initialValue="active"
          >
            <Select>
              <Option value="active">正常</Option>
              <Option value="paused">暂停</Option>
              <Option value="disabled">停用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={linkedAssets ? `关联资产 - ${linkedAssets.account_name}` : '关联资产'}
        open={assetsVisible}
        onCancel={() => {
          setAssetsVisible(false);
          setLinkedAssets(null);
        }}
        width={800}
        footer={null}
      >
        <Tabs
          items={[
            {
              key: 'ad_accounts',
              label: `广告账户 (${linkedAssets?.ad_accounts?.length ?? 0})`,
              children: (
                <Table
                  columns={adAccountColumns}
                  dataSource={linkedAssets?.ad_accounts || []}
                  rowKey="id"
                  loading={assetsLoading}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  size="small"
                  locale={{ emptyText: '暂无关联广告账户，请先同步 BM 总部数据再执行开发者同步' }}
                />
              ),
            },
            {
              key: 'pages',
              label: `已关联主页 (${linkedAssets?.pages?.length ?? 0})`,
              children: (
                <Table
                  columns={pageColumns}
                  dataSource={linkedAssets?.pages || []}
                  rowKey="id"
                  loading={assetsLoading}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  size="small"
                  locale={{ emptyText: '暂无关联公共主页，请先同步 BM 总部数据再执行开发者同步' }}
                />
              ),
            },
            {
              key: 'unassigned_pages',
              label: `未分配主页 (${linkedAssets?.unassigned_pages?.length ?? 0})`,
              children: (
                <>
                  <p style={{ marginBottom: 12, color: '#666', fontSize: 12 }}>
                    以下为开发者关联 BM 在总部表中的公共主页，且尚未分配给该开发者（按 BM + 主页 ID 匹配）
                  </p>
                  <Table
                    columns={pageColumns}
                    dataSource={linkedAssets?.unassigned_pages || []}
                    rowKey="id"
                    loading={assetsLoading}
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                    size="small"
                    locale={{ emptyText: '关联 BM 下的总部主页均已分配给该开发者' }}
                  />
                </>
              ),
            },
          ]}
        />
      </Modal>

      {/* 统计模态框 */}
      <Modal
        title="开发者账号统计"
        open={statsVisible}
        onCancel={() => setStatsVisible(false)}
        width={700}
        footer={null}
      >
        <Row gutter={16}>
          {stats.map(stat => (
            <Col span={12} key={stat.id}>
              <Card title={stat.name} size="small">
                <Statistic
                  title="使用率"
                  value={stat.usage_rate}
                  suffix="%"
                  precision={1}
                />
                <Progress
                  percent={stat.usage_rate}
                  status={stat.usage_rate >= 100 ? 'exception' : 'active'}
                />
                <p>今日调用：{stat.daily_api_calls} / {stat.daily_api_limit}</p>
                <p>状态：{stat.can_use ? <Tag color="green">可用</Tag> : <Tag color="red">不可用</Tag>}</p>
              </Card>
            </Col>
          ))}
        </Row>
      </Modal>
    </div>
  );
};

export default DeveloperAccounts;
